import { useMutation, useQuery } from '@apollo/client';
import { Button, Heading, Stack, Text, useNotifications } from '@sixfold/common-ui';
import React from 'react';
import { useNavigate, useParams } from 'react-router';

import { useIsAdmin } from '../../lib/authorization';
import {
  OvdTokenInfoFragment,
  OvdTokenQuery,
  OvdTokenQueryVariables,
  UpdateOvdTokenMutation,
  UpdateOvdTokenMutationVariables,
} from '../../lib/graphql';
import { Routes } from '../../routes';
import { OvdTokenForm, OvdTokenFormValues, parseCommaSeparated, toCommaSeparated } from '../components/ovd_token_form';
import {
  useOvdTokenTestFilter,
  OvdTokenTestFilterActions,
  OvdTokenTestFilterResults,
  buildTestFilterInput,
} from '../components/ovd_token_test_filter';
import { ovdTokenQuery, ovdTokensQuery, updateOvdTokenMutation } from '../graphql';

export const OvdTokenEditContainer = () => {
  const { token_id } = useParams() as { token_id: string };
  const { data, loading: queryLoading } = useQuery<OvdTokenQuery, OvdTokenQueryVariables>(ovdTokenQuery, {
    variables: { tokenId: token_id },
  });

  if (queryLoading) {
    return <Text>Loading...</Text>;
  }

  const token = data?.ovdToken;
  if (!token) {
    return <Text>Token not found.</Text>;
  }

  return <OvdTokenEditContent token={token} />;
};

const OvdTokenEditContent: React.FC<{ token: OvdTokenInfoFragment }> = ({ token }) => {
  const navigate = useNavigate();
  const notify = useNotifications();
  const isAdmin = useIsAdmin();

  const [updateToken, { loading: mutationLoading }] = useMutation<
    UpdateOvdTokenMutation,
    UpdateOvdTokenMutationVariables
  >(updateOvdTokenMutation);

  const { handleTest, loading: testLoading, result: testResult } = useOvdTokenTestFilter();

  const handleSubmit = async (values: OvdTokenFormValues) => {
    try {
      await updateToken({
        variables: {
          input: {
            tokenId: token.tokenId,
            label: values.label,
            platformIds: parseCommaSeparated(values.platformIds),
            shipperIds: parseCommaSeparated(values.shipperIds),
            carrierIds: parseCommaSeparated(values.carrierIds),
            deliveryCustomFields: parseCommaSeparated(values.deliveryCustomFields),
            tourCustomFields: parseCommaSeparated(values.tourCustomFields),
            enableStatus: values.enableStatus,
            enableApiEta: values.enableApiEta,
            enableCustomFields: values.enableCustomFields,
            enableCancellationReason: values.enableCancellationReason,
            enableLocation: values.enableLocation,
          },
        },
        refetchQueries: [{ query: ovdTokensQuery }, { query: ovdTokenQuery, variables: { tokenId: token.tokenId } }],
      });
    } catch (e) {
      console.error(e);
      notify.error({
        title: 'Failed to update OVD token',
        message: e instanceof Error ? e.message : undefined,
      });
      return;
    }
    notify.success({ title: 'OVD token updated' });
    void navigate(Routes.OvdTokens.generatePath({}));
  };

  return (
    <Stack direction="column" gap="medium">
      <Heading size="medium">Edit OVD Token: {token.label || '(no label)'}</Heading>
      {!isAdmin ? (
        <Stack direction="column" gap="small">
          <Text>
            <strong>Label:</strong> {token.label || '(no label)'}
          </Text>
          <Text>
            <strong>Platform IDs:</strong> {token.platformIds.join(', ')}
          </Text>
          <Text>
            <strong>Shipper IDs:</strong> {token.shipperIds.join(', ') || '(none)'}
          </Text>
          <Text>
            <strong>Carrier IDs:</strong> {token.carrierIds.join(', ') || '(none)'}
          </Text>
          <Text>
            <strong>Status:</strong> {token.enableStatus ? 'Enabled' : 'Disabled'}
          </Text>
          <Text>
            <strong>API ETA:</strong> {token.enableApiEta ? 'Enabled' : 'Disabled'}
          </Text>
          <Text>
            <strong>Custom Fields:</strong> {token.enableCustomFields ? 'Enabled' : 'Disabled'}
          </Text>
          <Text>
            <strong>Cancellation Reason:</strong> {token.enableCancellationReason ? 'Enabled' : 'Disabled'}
          </Text>
          <Text>
            <strong>Location:</strong> {token.enableLocation ? 'Enabled' : 'Disabled'}
          </Text>
          <Button kind="subtle" onClick={() => navigate(Routes.OvdTokens.generatePath({}))}>
            Back to List
          </Button>
        </Stack>
      ) : (
        <>
          <OvdTokenForm
            initialValues={{
              label: token.label ?? '',
              platformIds: toCommaSeparated(token.platformIds),
              shipperIds: toCommaSeparated(token.shipperIds),
              carrierIds: toCommaSeparated(token.carrierIds),
              deliveryCustomFields: toCommaSeparated(token.deliveryCustomFields),
              tourCustomFields: toCommaSeparated(token.tourCustomFields),
              enableStatus: token.enableStatus,
              enableApiEta: token.enableApiEta,
              enableCustomFields: token.enableCustomFields,
              enableCancellationReason: token.enableCancellationReason,
              enableLocation: token.enableLocation,
            }}
            onSubmit={handleSubmit}
            onCancel={() => navigate(Routes.OvdTokens.generatePath({}))}
            isSubmitting={mutationLoading}
            submitLabel="Update Token"
            extraActions={(formValues) => (
              <OvdTokenTestFilterActions
                onTest={() => {
                  void handleTest(
                    buildTestFilterInput({
                      platformIds: parseCommaSeparated(formValues.platformIds),
                      shipperIds: parseCommaSeparated(formValues.shipperIds),
                      carrierIds: parseCommaSeparated(formValues.carrierIds),
                      deliveryCustomFields: parseCommaSeparated(formValues.deliveryCustomFields),
                      tourCustomFields: parseCommaSeparated(formValues.tourCustomFields),
                    }),
                  );
                }}
                loading={testLoading}
                matchingShipmentCount={testResult?.matchingShipmentCount ?? null}
              />
            )}
          />
          <OvdTokenTestFilterResults result={testResult} />
        </>
      )}
    </Stack>
  );
};
